import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import { Button } from '@mui/material';

import { selectSortedCommunicationTemplates } from '../../../../store/communicationSelectors';
import {
    clearCommunicationTemplates,
    clearCurrentTemplate,
    clearPreviewCommunication,
    loadCommunicationTemplate,
    previewParticipantEmailCommunication,
    searchCommunicationTemplates,
    sendParticipantEmailCommunication
} from '../../../../store/communicationService';
import { loadCommunicationTypes } from '../../../../store/directusService';
import { loadParticipantDocumentDetails } from '../../../../store/documentService';
import { loadServiceConstants } from '../../../../store/participantService';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SELF_EMPLOYED_COACH,
    SUPERUSER
} from '../../../../utils/userRoles';
import CommunicationsErrorModal from '../../../communication/CommunicationsErrorModal';
import SingleSelect from '../../../formElements/SingleSelect';
import IconError from '../../../IconError';
import LabelledTextField from '../../../ui/editors/LabelledTextField';
import SensitivityNotice from '../../../ui/notices/sensitivity/SensitivityNotice';
import DDLOptionPicker from '../../../ui/pickers/DDLOptionPicker';
import MultiSelect from '../../../ui/pickers/MultiSelect';

import { initialErrorState, validate } from './validateCommunications';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';
import local from '../communicationStyles.module.css';

const EmailCommunication = () => {
    const dispatch = useDispatch();
    const { state } = useLocation();

    // LOCAL STATE
    const TIME = 30;
    const acceptedRoles = [
        ADVISER,
        MANAGER,
        QUALITY,
        RECRUITMENT_MANAGER,
        SELF_EMPLOYED_COACH,
        SUPERUSER
    ];
    const documentRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
    const initialState = {
        fromEmailAddress: '',
        emailAddress: '',
        templateId: '',
        templateName: '',
        subject: '',
        details: '',
        signature: '',
        sigLen: 0,
        attachmentIds: []
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState(initialErrorState);
    const [isSucceeded, setIsSucceeded] = useState(false);
    const [showData, setShowData] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [preSelectedDocuments, setPreSelectedDocuments] = useState([]);
    const [preSelectedDocumentIds, setPreSelectedDocumentIds] = useState([]);
    const [isClearSelectedDocuments, setIsClearSelectedDocuments] = useState('0');
    const [isSendButtonDisabled, setIsSendButtonDisabled] = useState(false);
    const [placeholderErrorOpen, setPlaceholderErrorOpen] = useState(false);
    const [fromEmailAddresses, setFromEmailAddresses] = useState([]);
    const [canAttachDocuments, setCanAttachDocuments] = useState(false);
    const msg = 'Email Communication sent to participant';

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const communicationTypes = useSelector(
        (state) => state.entities.directusService.communicationTypes
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const templates = useSelector(selectSortedCommunicationTemplates);
    const currentTemplate = useSelector(
        (state) => state.entities.communicationService.communicationTemplate
    );
    const participantDocuments = useSelector(
        (state) => state.entities.documentService.participantDocumentDetails
    );
    const serviceConstants = useSelector(
        (state) => state.entities.participantService.serviceConstants
    );
    const communicationPreview = useSelector(
        (state) => state.entities.communicationService.communicationPreview
    );
    const communicationPreviewLoading = useSelector(
        (state) => state.entities.communicationService.communicationPreviewLoading
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (communicationTypes?.length < 1) dispatch(loadCommunicationTypes());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (!loggedInUser?.id) return;
        const signature = configSignature();
        setNewEntry((prev) => ({
            ...prev,
            signature,
            sigLen: signature.length,
            details: newEntry.details.length ? newEntry.details : newEntry.details.concat(signature)
        }));
        setCanAttachDocuments(hasRole(documentRoles, roles));
    }, [loggedInUser, roles]);

    useEffect(() => {
        if (!currentParticipant || Object.keys(currentParticipant).length < 1) return;
        dispatch(loadParticipantDocumentDetails(currentParticipant.id));
        setNewEntry((prev) => ({ ...prev, emailAddress: currentParticipant.emailAddress }));
        dispatch(
            searchCommunicationTemplates({
                type: 'Email',
                serviceIds: [currentParticipant.serviceId],
                inactive: false
            })
        );
    }, [currentParticipant]);

    useEffect(() => {
        if (!serviceConstants[currentParticipant.serviceId]) {
            dispatch(loadServiceConstants(currentParticipant.serviceId));
            return;
        }
        if (!loggedInUser?.id || !currentParticipant?.serviceId) {
            setFromEmailAddresses([]);
            setNewEntry((prev) => ({ ...prev, fromEmailAddress: '' }));
            return;
        }
        const alternativeEmail =
            serviceConstants[currentParticipant.serviceId]?.alternativeFromEmailAddress;
        let fromEmails = [
            {
                name: loggedInUser.communicationEmailAddress,
                id: loggedInUser.communicationEmailAddress
            }
        ];
        if (alternativeEmail)
            fromEmails = [...fromEmails, { name: alternativeEmail, id: alternativeEmail }];
        setFromEmailAddresses(fromEmails);

        let foundTemplate;
        if (templates?.length > 0 && state) {
            const constants = serviceConstants[currentParticipant.serviceId];
            if (constants) {
                if (
                    state.status?.toLowerCase() === 'not known' &&
                    constants?.notKnownEmailTemplateId
                ) {
                    foundTemplate = templates.find(
                        (el) => el.id === constants?.notKnownEmailTemplateId
                    );
                } else if (
                    state.status?.toLowerCase() === 'failed to attend' &&
                    constants?.ftaEmailTemplateId
                ) {
                    foundTemplate = templates.find((el) => el.id === constants?.ftaEmailTemplateId);
                }
            }
        }
        if (foundTemplate) dispatch(loadCommunicationTemplate(foundTemplate.id));
        setNewEntry((prev) => ({
            ...prev,
            fromEmailAddress: fromEmails[0].id,
            templateId: foundTemplate?.id ?? newEntry.templateId,
            templateName: foundTemplate?.name ?? newEntry.templateName
        }));
    }, [currentParticipant, serviceConstants, loggedInUser, templates]);

    useEffect(() => {
        if (!participantDocuments) return;
        const newDocuments = participantDocuments.map((el) => ({ ...el, name: el.filename }));
        setDocuments(newDocuments);
    }, [participantDocuments]);

    useEffect(() => {
        if (
            !newEntry.templateId ||
            Object.keys(currentTemplate).length < 1 ||
            communicationTypes?.length < 1
        )
            return;
        if (communicationTypes.find((el) => el.id === currentTemplate.typeId)?.name !== 'Email')
            return;

        const newErrors = validate(newEntry, errors, 'Email').newErrors;
        setErrors({ ...errors, fromEmailAddress: newErrors.fromEmailAddress });
        if (newErrors.fromEmailAddress.error) return;
        const details = currentTemplate.details.concat(newEntry.signature);
        if (details && details.includes('{{'))
            dispatch(
                previewParticipantEmailCommunication(
                    {
                        subject: currentTemplate.subject,
                        body: details,
                        attachmentIds: newEntry.attachmentIds,
                        fromEmailAddress: newEntry.fromEmailAddress,
                        ...(state?.appointmentId && { appointmentId: state.appointmentId }),
                        ...(newEntry.templateId && { communicationTemplateId: newEntry.templateId })
                    },
                    currentParticipant.id
                )
            );
        else
            setNewEntry((prev) => ({
                ...prev,
                subject: currentTemplate.subject,
                details: details
            }));
    }, [newEntry.templateId, currentTemplate, communicationTypes]);

    useEffect(() => {
        if (communicationPreview.body && !placeholderErrorOpen)
            setNewEntry((prev) => ({
                ...prev,
                subject: communicationPreview.subject,
                details: communicationPreview.body
            }));
    }, [communicationPreview]);

    useEffect(() => {
        if (successMessage === msg) {
            setIsSendButtonDisabled(false);
            clearForm();
        }
    }, [successMessage]);

    // HELPER FNS
    const clearForm = () => {
        const signature = configSignature();
        setNewEntry({
            ...initialState,
            signature,
            details: newEntry.details.slice(-(signature.length - 1)),
            fromEmailAddress: newEntry.fromEmailAddress
        });
        setPreSelectedDocuments([]);
        setPreSelectedDocumentIds([]);
        setIsClearSelectedDocuments(Math.random());
        setErrors(initialErrorState);
        setIsSucceeded(false);
        dispatch(clearCurrentTemplate());
        dispatch(clearPreviewCommunication());
    };

    const configSignature = () => {
        const gap = `<br />`;
        const name = `<p>${loggedInUser?.firstName} ${loggedInUser?.lastName}</p>`;
        const job = `<p>${loggedInUser?.jobTitle}</p>`;
        const phone = `<p>${loggedInUser?.phoneNumber}</p>`;
        return `${gap}${name}${loggedInUser?.jobTitle ? job : ''}${
            loggedInUser?.phoneNumber ? phone : ''
        }`;
    };

    const clearError = (key) => {
        setErrors((prev) => ({
            ...prev,
            [key]: { error: false, message: '' }
        }));
    };

    const cleanUp = () => {
        dispatch(clearCurrentTemplate());
        dispatch(clearPreviewCommunication());
        dispatch(clearCommunicationTemplates());
    };

    // EVENT HANDLERS
    const processedPlaceholders = (newMessage) => {
        setNewEntry((prev) => ({
            ...prev,
            details: newMessage
        }));
    };

    const onRevealData = () => {
        setShowData(!showData);
        setTimeout(() => setShowData((showData) => !showData), TIME * 1000);
    };

    const onDetailsChange = (details) => {
        clearError('details');
        setNewEntry((prev) => ({ ...prev, details }));
    };

    const onFromEmailAddressChange = (emailAddress) => {
        clearError('fromEmailAddress');
        setNewEntry((prev) => ({ ...prev, fromEmailAddress: emailAddress }));
    };

    const onPreviewClick = () => {
        const { isValid, newErrors } = validate(newEntry, errors, 'Email');
        setErrors(newErrors);
        if (!isValid) return;
        if (newEntry.details && newEntry.details.includes('{{')) setPlaceholderErrorOpen(true);
        else onSubmit();
    };

    const onSubmit = () => {
        setIsSendButtonDisabled(true);
        dispatch(
            sendParticipantEmailCommunication(
                {
                    subject: newEntry.subject,
                    body: newEntry.details.replaceAll('&nbsp;', ' '),
                    attachmentIds: newEntry.attachmentIds,
                    fromEmailAddress: newEntry.fromEmailAddress,
                    ...(state?.appointmentId && { appointmentId: state.appointmentId }),
                    ...(newEntry.templateId && { communicationTemplateId: newEntry.templateId })
                },
                currentParticipant.id,
                msg
            )
        );
    };

    return (
        <div className={app.pageContainer} id="emailScreen">
            <CommunicationsErrorModal
                isOpen={placeholderErrorOpen}
                participants={[currentParticipant]}
                payload={{
                    subject: newEntry.subject,
                    body: newEntry.details,
                    attachmentIds: newEntry.attachmentIds,
                    fromEmailAddress: newEntry.fromEmailAddress,
                    ...(state?.appointmentId && { appointmentId: state.appointmentId }),
                    ...(newEntry.templateId && { communicationTemplateId: newEntry.templateId })
                }}
                previewCommunication={previewParticipantEmailCommunication}
                onAccept={onSubmit}
                onClose={() => setPlaceholderErrorOpen(false)}
                onMessageProcessed={processedPlaceholders}
            />
            <h1 className={app.mainHeading}>Communications</h1>
            <form className={`${form.formWrapper} ${local.formContainer}`}>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={`${form.textAreaSet} ${form.staticMinHeight}`}>
                            <label>To Email Address</label>
                            <div className={form.textAreaText}>
                                {currentParticipant.emailAddress}
                            </div>
                        </div>
                        <SingleSelect
                            id="fromEmailAddress"
                            label="From Email Address"
                            placeholder="Select an email address"
                            mandatory={true}
                            capitalizeList={false}
                            menuItems={fromEmailAddresses}
                            selectedId={newEntry.fromEmailAddress || ''}
                            selected={fromEmailAddresses.find(
                                (el) => el.id === newEntry.fromEmailAddress
                            )}
                            onChange={(emailAddress) => onFromEmailAddressChange(emailAddress)}
                            error={errors.fromEmailAddress}
                        />

                        {!showData && (
                            <div className={`${form.inputCover} ${form.coverHeightMed}`}>
                                <SensitivityNotice
                                    onClick={onRevealData}
                                    numRows={3}
                                    customClass="inputNotice"
                                />
                            </div>
                        )}
                    </div>
                    <div className={form.formColumn}>
                        <div className={form.alignDownMed}>
                            <DDLOptionPicker
                                label={'Template'}
                                id={'templates'}
                                disabled={!hasRole(acceptedRoles, roles)}
                                menuItems={templates || []}
                                chosenName={newEntry.templateName}
                                chosenId={newEntry.templateId}
                                onChange={(chosenId) => {
                                    clearError('templateId');
                                    chosenId && dispatch(loadCommunicationTemplate(chosenId));
                                    const chosenTemplate = templates?.find(
                                        (el) => el.id === chosenId
                                    );
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        templateId: chosenId || '',
                                        templateName: chosenTemplate?.name || ''
                                    }));
                                }}></DDLOptionPicker>
                        </div>
                        <div className={form.alignDownMed}>
                            <LabelledTextField
                                label={'Email Subject'}
                                id={'subject'}
                                mandatory={true}
                                disabled={!hasRole(acceptedRoles, roles)}
                                value={newEntry.subject}
                                placeholder={'Enter email subject'}
                                onChange={(e) => {
                                    clearError('subject');
                                    setNewEntry((prev) => ({
                                        ...prev,
                                        subject: e.target.value
                                    }));
                                }}
                            />
                            {errors.subject.error && (
                                <div className={form.textInputError}>
                                    <IconError text={errors.subject} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={`${form.formSection} ${form.alignDown}`}>
                    <div className={form.formColumn}>
                        <div className={form.textAreaSet}>
                            <ReactQuill
                                value={newEntry.details}
                                modules={EmailCommunication.modules}
                                placeholder="Enter details"
                                onChange={onDetailsChange}
                            />
                            {errors.details.error && (
                                <div className={`${form.textInputError} ${form.resetMargin}`}>
                                    <IconError text={errors.details} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={local.attachIcon}>
                            <AttachFileOutlinedIcon />
                            Attachments
                        </div>

                        <MultiSelect
                            id="documents"
                            label="Documents"
                            key={isClearSelectedDocuments}
                            placeholder="PT Document Storage"
                            disabled={!canAttachDocuments}
                            menuItems={documents || []}
                            preSelectedIds={preSelectedDocumentIds}
                            preSelects={preSelectedDocuments}
                            onChange={(ids) => {
                                setNewEntry((prev) => ({
                                    ...prev,
                                    attachmentIds: ids.length === 0 ? [] : ids
                                }));
                            }}
                        />
                    </div>
                </div>

                <div className={form.buttonRight}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        onClick={(e) => {
                            e.preventDefault();
                            onPreviewClick();
                        }}
                        disabled={
                            !hasRole(acceptedRoles, roles) ||
                            newEntry.details.length < 1 ||
                            isSendButtonDisabled ||
                            Object.values(errors).some((el) => el.error) ||
                            communicationPreviewLoading
                        }>
                        Send Now
                    </Button>
                </div>
                {isSucceeded && <div className={form.successMessage}>Email sent</div>}
            </form>
        </div>
    );
};

export default EmailCommunication;

EmailCommunication.modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        ['clean']
    ]
};
